import { useRef, useState } from 'react';
import { cn } from '@/lib/core';
import { Icon } from '@/components/ui/icon';
import { resolveMediaUrl } from '../ui/resolveMediaUrl';

interface HeroBannerProps {
  images: Array<string | null | undefined>;
  logoUrl?: string | null;
  name?: string | null;
  variant?: 'standard' | 'experience';
  titleAccent?: string | null;
  bookingType?: string | null;
  venueGroupName?: string | null;
}

export function HeroBanner({
  images,
  logoUrl,
  name,
  variant = 'standard',
  titleAccent,
  bookingType,
  venueGroupName,
}: HeroBannerProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const resolvedImages = images.map(resolveMediaUrl).filter((u): u is string => !!u);
  const resolvedLogo = resolveMediaUrl(logoUrl);

  function handleScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const index = Math.round(el.scrollLeft / el.clientWidth);
    setActiveIndex(Math.min(index, resolvedImages.length - 1));
  }

  return (
    <div className="relative h-[65svh] w-full">
      {/* Image carousel */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="scrollbar-hide flex h-full w-full snap-x snap-mandatory overflow-x-auto"
      >
        {resolvedImages.length > 0 ? (
          resolvedImages.map((url, i) => (
            <div key={i} className="h-full w-full shrink-0 snap-start">
              <img
                src={url}
                alt={name ?? ''}
                className="h-full w-full object-cover"
                loading={i === 0 ? 'eager' : 'lazy'}
              />
            </div>
          ))
        ) : (
          <div className="bg-jncquoi-neutral-100 h-full w-full shrink-0" />
        )}
      </div>

      {/* Gradient overlay */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            variant === 'experience'
              ? 'linear-gradient(180deg, rgba(0,0,0,0.45) 3.64%, rgba(0,0,0,0.1) 30%, rgba(0,0,0,0.55) 100%)'
              : 'linear-gradient(180deg, rgba(0,0,0,0.45) 3.64%, rgba(0,0,0,0.045) 21.79%, rgba(0,0,0,0.045) 49.62%, rgba(0,0,0,0.9) 79.62%)',
        }}
      />

      {/* Bottom: logo, or name + titleAccent, plus experience meta rows and dots */}
      <div className="absolute right-0 bottom-0 left-0 z-10 flex flex-col items-center gap-[34px] px-4 pb-12">
        {resolvedLogo ? (
          <img
            src={resolvedLogo}
            alt={name ?? ''}
            className="h-[130px] w-[304px] object-contain mix-blend-lighten"
          />
        ) : (
          <div className="flex flex-col items-center gap-1 text-center">
            {name && (
              <p className="font-didot-lt-std text-jncquoi-neutral-00 text-[40px] leading-[40px] font-black tracking-[-1.6px]">
                {name}
              </p>
            )}
            {titleAccent && (
              <p className="font-didot-lt-std text-jncquoi-neutral-00 text-[48px] leading-normal font-bold tracking-[-2px] italic">
                {titleAccent}
              </p>
            )}
          </div>
        )}

        {variant === 'experience' && (bookingType || venueGroupName) && (
          <div className="flex flex-col items-center gap-4">
            {bookingType && (
              <span className="font-pp-neue-montreal text-jncquoi-neutral-00 flex items-center gap-2 text-[16px] tracking-[0.25px] uppercase">
                <Icon name="calendar" width={20} height={20} strokeWidth={1.5} />
                {bookingType}
              </span>
            )}

            {venueGroupName && (
              <span className="font-pp-neue-montreal text-jncquoi-neutral-00 flex items-center gap-2 text-[16px] tracking-[0.5px]">
                <Icon name="map-pin" width={20} height={20} strokeWidth={1.5} />
                {venueGroupName}
              </span>
            )}
          </div>
        )}

        {resolvedImages.length > 1 && (
          <div className="flex items-center gap-2" aria-hidden="true">
            {resolvedImages.map((_, i) => (
              <span
                key={i}
                className={cn(
                  'block h-px w-3.5 transition-colors duration-300',
                  i === activeIndex ? 'bg-jncquoi-neutral-900' : 'bg-jncquoi-neutral-300'
                )}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
